
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useFilteredInsights } from "@/hooks/useFilteredInsights";
import InsightCard from "@/components/insights/InsightCard";
import { InsightCard as InsightCardType } from "@/types/insight";

const FeaturedInsightsCarousel = () => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const { insights: fetchedInsights, isLoading } = useFilteredInsights(null, "KY", 8);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);
  const [insights, setInsights] = useState<InsightCardType[]>([]);
  
  // Transform fetched insights to match InsightCard type
  useEffect(() => {
    if (fetchedInsights && fetchedInsights.length > 0) {
      const transformedInsights: InsightCardType[] = fetchedInsights.map(insight => ({
        id: insight.id,
        title: insight.title,
        category: insight.category?.name || '',
        agreePercentage: insight.agree_percentage || 0,
        totalPollsters: insight.total_pollsters || 0,
        description: insight.content || '',
        status: insight.published ? 'published' : 'draft',
        country_code: insight.country_code,
        constituency_code: null,
        linked_poll: insight.linked_poll,
        stats_status: insight.stats_status,
        slug: insight.slug || null
      }));
      setInsights(transformedInsights);
    }
  }, [fetchedInsights]);
  
  // Function to handle card selection for modal view
  const handleViewInsights = (card: InsightCardType) => {
    // This would typically open a modal, but for now we'll just console log
    console.log("View insight details:", card);
    // In a real implementation, you would set a state to open a modal with the card details
  };
  
  // Update visible cards based on screen width
  useEffect(() => {
    const updateVisibleCards = () => {
      if (window.innerWidth >= 1280) {
        setVisibleCards(4);
      } else if (window.innerWidth >= 768) {
        setVisibleCards(3);
      } else if (window.innerWidth >= 640) {
        setVisibleCards(2);
      } else {
        setVisibleCards(1);
      }
    };

    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);
  
  // Scroll to the left
  const scrollLeft = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      carouselRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  
  // Scroll to the right
  const scrollRight = () => {
    if (insights && currentIndex < insights.length - visibleCards) {
      setCurrentIndex(currentIndex + 1);
      carouselRef.current?.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 px-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Featured Insights
            </h2>
            <p className="text-muted-foreground">
              The latest trends and issues in the Cayman Islands
            </p>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollLeft}
              disabled={currentIndex === 0 || isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollRight}
              disabled={!insights || currentIndex >= insights.length - visibleCards || isLoading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="relative">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div 
              ref={carouselRef}
              className="flex overflow-x-auto snap-x no-scrollbar gap-6 pb-4"
              style={{ scrollBehavior: 'smooth' }}
            >
              {insights && insights.map((card) => (
                <div 
                  key={card.id}
                  className="flex-none w-[calc(100%-2rem)] sm:w-[calc(50%-1rem)] md:w-[calc(33.33%-1rem)] lg:w-[calc(25%-1rem)] snap-start"
                >
                  <InsightCard card={card} onViewInsights={handleViewInsights} />
                </div>
              ))}
              
              {(!insights || insights.length === 0) && (
                <div className="w-full text-center p-8 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <p>No insights available at this time. Check back soon!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedInsightsCarousel;
